import React, { useEffect } from 'react';
import { useField } from 'react-form';

import type { Validator } from '~/utils/forms';
import CheckBox, { type CheckBoxProps } from './CheckBox';
import FormGroup, { type FormGroupProps } from './FormGroup';
import { classNames, makeStyles } from '../../../utils/withStyles';
import { getReadability, readableFontColor } from '../../../utils/colors';
import { toImportant } from '../../../utils/styles';

const useStyles = makeStyles(theme => ({
  textSecondary: {
    color: getReadability(theme.palette.background.default, theme.palette.primary.main) < 1.25 ? toImportant(readableFontColor(theme.palette.primary.main)) : undefined,
  },
}));

interface CheckBoxGroupProps<TValue> extends Omit<Partial<CheckBoxProps>, 'field' | 'onChange' | 'style' | 'value'> {
  field: string;
  formGroupProps?: Partial<Omit<FormGroupProps, 'children' | 'style'>> | null;
  inline?: boolean;
  // @deprecated Not used, only kept here to avoid compilation errors
  offset?: never;
  onChange?: ((value: TValue) => void) | null;
  setFieldError?: (error: string | boolean | null | undefined) => void;
  style?: React.CSSProperties | null;
  validate?: Validator<TValue>;
  // defaults to true
  value?: TValue;
}

const CheckBoxGroup = <TValue = boolean, >({
  'data-tour-id': dataTourId = null,
  disabled = false,
  field,
  formGroupProps = null,
  inline = false,
  // only here to make sure offset isn't passed through to input
  offset: _exclude,
  onChange = null,
  radioCheck = false,
  rootClassName,
  setFieldError,
  style = null,
  title = null,
  titleTooltip = null,
  validate,
  value: valueProp = true as TValue,
  ...inputProps
}: CheckBoxGroupProps<TValue>) => {
  const { value, setValue, meta: { error } } = useField<TValue>(field, { validate });
  const classes = useStyles();

  useEffect(() => {
    if (typeof setFieldError === 'function') {
      setFieldError(error);
    }
  }, [error, setFieldError]);

  return (
    <FormGroup {...formGroupProps} style={{ ...(style), display: inline ? 'inline' : 'block' }}>
      <CheckBox
        checked={!!value}
        data-tour-id={dataTourId}
        disabled={disabled}
        field={field}
        rootClassName={classNames(classes.textSecondary, rootClassName)}
        onChange={() => {
          const uncheckedValue = (valueProp === true ? false : null) as TValue;
          const nextValue = String(value || null) === String(valueProp || null) ? uncheckedValue : valueProp;
          if (typeof onChange === 'function') {
            onChange(nextValue);
          }
          setValue(nextValue);
        }}
        radioCheck={radioCheck}
        title={title}
        titleTooltip={titleTooltip}
        {...inputProps}
      />
    </FormGroup>
  );
};

export default CheckBoxGroup;
